import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

/**
 * Agent-type display labels, shared by server and client components.
 *
 * Lives in lib/ (no "use client") deliberately: importing a value from a
 * client module into a server component yields a client-reference proxy
 * whose property lookups silently return undefined — the (since removed)
 * /use-cases pages rendered raw slugs ("pa") until these moved here. Client components
 * (handoff-card etc.) import from here too; handoff-card re-exports for
 * its existing consumers.
 *
 * Two shapes on purpose: TYPE_LABELS stays plain English for the
 * English-only-by-policy admin surface (inspiration-review-shell) and the
 * existing onboarding consumers; TYPE_LABEL_MSGS carries the same labels as
 * Lingui descriptors for localized public chrome — resolve with
 * `i18n._(...)`.
 */
export const TYPE_LABELS: Record<string, string> = {
  pa: "Personal Assistant",
  pm: "Project Leader",
  qa: "QA Specialist",
  ops: "Operations Manager",
};

export const TYPE_LABEL_MSGS: Record<string, MessageDescriptor> = {
  pa: msg`Personal Assistant`,
  pm: msg`Project Leader`,
  qa: msg`QA Specialist`,
  ops: msg`Operations Manager`,
  la: msg`Language Teacher`,
};

/**
 * Localized label descriptor for an agent-type slug, resolving legacy
 * "<name>-<type>" slugs (quinn-qa, nora-pm, peggy-pa) via their type suffix.
 * Undefined for unknown slugs — callers fall back to the DB `role_label`
 * (agent_types seed data is English, same class as the desk built-in titles;
 * a future agent type without a descriptor here degrades to its stored
 * English label instead of breaking).
 */
export const typeLabelMsg = (slug: string): MessageDescriptor | undefined =>
  TYPE_LABEL_MSGS[slug] ?? TYPE_LABEL_MSGS[slug.split("-").at(-1) ?? ""];
